import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';




const RELATIONSHIPS = [
  'Spouse', 'Partner', 'Son', 'Daughter', 'Father', 'Mother',
  'Brother', 'Sister', 'Attorney', 'Accountant', 'Financial Advisor',
  'Trusted Friend', 'Other'
];

export default function Guardians({ session }) {
  const [vault, setVault] = useState(null);
  const [guardians, setGuardians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [guardianCount, setGuardianCount] = useState(3);
  const [form, setForm] = useState({ name: '', email: '', relationship: 'Spouse' });
  

  const load = useCallback(async () => {
    const { data: vaultData } = await supabase
      .from('vaults').select('*').eq('owner_id', session.user.id).single();
    setVault(vaultData);
    if (vaultData) setGuardianCount(vaultData.guardian_count || 3);

    const { data: guardianData } = await supabase
      .from('vault_guardians').select('*')
      .eq('owner_id', session.user.id)
      .order('created_at', { ascending: true });
    setGuardians(guardianData || []);
    setLoading(false);
  }, [session]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddGuardian = async (e) => {
    e.preventDefault();
    setError('');
    if (guardians.length >= guardianCount) {
      setError(`You've reached your maximum of ${guardianCount} Vault Guardians.`);
      return;
    }
    if (guardians.find(g => g.guardian_email === form.email)) {
      setError('This email is already a Vault Guardian.');
      return;
    }
    setSaving(true);
    const { error: err } = await supabase.from('vault_guardians').insert({
      vault_id: vault.id,
      owner_id: session.user.id,
      guardian_email: form.email,
      guardian_name: form.name,
      guardian_relationship: form.relationship,
      status: 'invited',
    });
    if (err) { setError(err.message); setSaving(false); return; }
    setForm({ name: '', email: '', relationship: 'Spouse' });
    setShowForm(false);
    setSaving(false);
    setSuccess(`${form.name} has been added as a Vault Guardian.`);
    setTimeout(() => setSuccess(''), 4000);
    load();
  };

  const handleRemove = async (id, name) => {
    if (!window.confirm(`Remove ${name} as a Vault Guardian?`)) return;
    await supabase.from('vault_guardians').delete().eq('id', id);
    load();
  };

  const handleUpdateCount = async (count) => {
    setGuardianCount(count);
    await supabase.from('vaults').update({
      guardian_count: count,
      guardian_threshold: 3,
    }).eq('id', vault.id);
  };

  const handleSendInvite = async (guardian) => {
    setSendingInvite(guardian.id);
    // In production this would trigger a Supabase Edge Function to send email
    // For now we simulate the send
    await new Promise(r => setTimeout(r, 1500));
    await supabase.from('vault_guardians').update({ invited_at: new Date().toISOString() })
      .eq('id', guardian.id);
    setSendingInvite(null);
    setSuccess(`Invite sent to ${guardian.guardian_name}.`);
    setTimeout(() => setSuccess(''), 4000);
    load();
  };

  const thresholdText = guardianCount === 3 ? '3 of 3' : '3 of 4';
  const isSetupComplete = guardians.length >= 3;

  const style = `
    .grd em::after { content: none !important; }
    .grd { padding: 40px 32px; max-width: 800px; margin: 0 auto; }

    /* HEADER */
    .grd-header { margin-bottom: 32px; }
    .grd-eyebrow { font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #9CA3AF; margin-bottom: 8px; }
    .grd-title { font-family: 'Fraunces', serif; font-weight: 900; font-size: 36px; color: #1E1040; line-height: 1.05; margin-bottom: 8px; }
    .grd-title em { font-style: italic; color: #8B5CF6; }
    .grd-title em::after { content: none !important; }
    .grd-sub { font-size: 15px; color: #374151; line-height: 1.6; max-width: 560px; }

    /* EXPLAINER CARD */
    .grd-explainer {
      background: linear-gradient(135deg, rgba(139,92,246,0.06), rgba(56,189,248,0.04));
      border: 1px solid rgba(139,92,246,0.15); border-radius: 20px;
      padding: 28px 32px; margin-bottom: 28px;
    }
    .grd-explainer-title {
      font-family: 'Fraunces', serif; font-weight: 900; font-size: 18px;
      color: #1E1040; margin-bottom: 16px;
    }
    .grd-explainer-steps { display: flex; flex-direction: column; gap: 12px; }
    .grd-step {
      display: flex; align-items: flex-start; gap: 14px;
    }
    .grd-step-num {
      width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0;
      background: linear-gradient(135deg, #8B5CF6, #38BDF8);
      display: flex; align-items: center; justify-content: center;
      font-size: 12px; font-weight: 800; color: #fff;
    }
    .grd-step-text { font-size: 14px; color: #1F2937; line-height: 1.5; padding-top: 4px; }
    .grd-step-text strong { color: #111827; }

    /* RULE SELECTOR */
    .grd-rule-wrap { margin-bottom: 28px; }
    .grd-rule-label { font-size: 12px; font-weight: 700; color: #6B7280; letter-spacing: 0.06em; text-transform: uppercase; margin-bottom: 12px; }
    .grd-rule-options { display: flex; gap: 12px; }
    .grd-rule-option {
      flex: 1; padding: 16px 20px; border-radius: 14px; cursor: pointer;
      border: 2px solid #EDE9E3; background: #fff;
      transition: all 0.2s; text-align: center;
    }
    .grd-rule-option:hover { border-color: rgba(139,92,246,0.3); }
    .grd-rule-option.selected { border-color: #8B5CF6; background: rgba(139,92,246,0.04); }
    .grd-rule-option-title { font-size: 20px; font-weight: 800; color: #111827; margin-bottom: 4px; }
    .grd-rule-option.selected .grd-rule-option-title { color: #6D28D9; }
    .grd-rule-option-sub { font-size: 12px; color: #4B5563; line-height: 1.4; }
    .grd-rule-option.selected .grd-rule-option-sub { color: #374151; }

    /* GUARDIAN LIST */
    .grd-list-header {
      display: flex; align-items: center; justify-content: space-between;
      margin-bottom: 14px;
    }
    .grd-list-title { font-size: 13px; font-weight: 700; color: #374151; letter-spacing: 0.04em; text-transform: uppercase; }
    .grd-list-count {
      font-size: 12px; font-weight: 600; color: #4B5563;
    }
    .grd-list-count.ready { color: #16A34A; }

    .grd-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 20px; }

    /* GUARDIAN CARD */
    .grd-card {
      background: #fff; border: 1.5px solid #EAE5F5; border-radius: 16px;
      padding: 18px 20px; display: flex; align-items: center; gap: 16px;
      box-shadow: 0 2px 8px rgba(139,92,246,0.04);
      transition: border-color 0.2s;
    }
    .grd-card.accepted { border-color: rgba(34,197,94,0.3); }
    .grd-card-avatar {
      width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
      background: linear-gradient(135deg, #8B5CF6, #38BDF8);
      display: flex; align-items: center; justify-content: center;
      font-size: 18px; font-weight: 800; color: #fff;
    }
    .grd-card.accepted .grd-card-avatar { background: linear-gradient(135deg, #22C55E, #16A34A); }
    .grd-card-info { flex: 1; min-width: 0; }
    .grd-card-name { font-size: 15px; font-weight: 700; color: #1E1040; }
    .grd-card-meta { font-size: 13px; color: #374151; margin-top: 2px; }
    .grd-card-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }

    /* STATUS BADGE */
    .grd-badge {
      font-size: 11px; font-weight: 700; padding: 4px 12px; border-radius: 100px;
    }
    .grd-badge.invited { background: rgba(251,146,60,0.1); color: #D97706; }
    .grd-badge.accepted { background: rgba(34,197,94,0.1); color: #16A34A; }
    .grd-badge.declined { background: rgba(239,68,68,0.1); color: #DC2626; }

    .grd-invite-btn {
      padding: 6px 14px; border-radius: 100px; border: 1.5px solid rgba(139,92,246,0.3);
      background: transparent; color: #7C3AED;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 12px; font-weight: 600;
      cursor: pointer; transition: all 0.2s; white-space: nowrap;
    }
    .grd-invite-btn:hover { background: rgba(139,92,246,0.06); }
    .grd-invite-btn:disabled { opacity: 0.5; cursor: not-allowed; }

    .grd-remove-btn {
      width: 28px; height: 28px; border-radius: 8px; border: none;
      background: rgba(239,68,68,0.06); color: #EF4444;
      font-size: 14px; cursor: pointer; display: flex; align-items: center; justify-content: center;
      transition: background 0.2s;
    }
    .grd-remove-btn:hover { background: rgba(239,68,68,0.12); }

    /* EMPTY SLOT */
    .grd-empty-slot {
      background: #FDFCFF; border: 2px dashed #EAE5F5; border-radius: 16px;
      padding: 18px 20px; display: flex; align-items: center; gap: 16px;
      cursor: pointer; transition: all 0.2s;
    }
    .grd-empty-slot:hover { border-color: #8B5CF6; background: rgba(139,92,246,0.02); }
    .grd-empty-slot-icon {
      width: 44px; height: 44px; border-radius: 12px; flex-shrink: 0;
      background: #F5F3FF; border: 2px dashed #C4B5FD;
      display: flex; align-items: center; justify-content: center;
      font-size: 20px; color: #A78BFA;
    }
    .grd-empty-slot-text { font-size: 14px; font-weight: 600; color: #6D28D9; }
    .grd-empty-slot-sub { font-size: 12px; color: #7C3AED; margin-top: 2px; }

    /* ADD FORM */
    .grd-form {
      background: #fff; border: 1.5px solid #EAE5F5; border-radius: 16px;
      padding: 24px; margin-bottom: 20px;
      box-shadow: 0 4px 16px rgba(139,92,246,0.06);
    }
    .grd-form-title { font-size: 15px; font-weight: 700; color: #1E1040; margin-bottom: 20px; }
    .grd-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
    @media(max-width:600px){ .grd-form-grid { grid-template-columns: 1fr; } }
    .grd-field { display: flex; flex-direction: column; gap: 6px; }
    .grd-field-label { font-size: 12px; font-weight: 700; color: #374151; }
    .grd-field-input, .grd-field-select {
      padding: 11px 14px; border: 1.5px solid #E5E7EB; border-radius: 10px;
      font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #1E1040;
      background: #FAFAFA; outline: none; transition: border-color 0.2s;
      width: 100%;
    }
    .grd-field-input:focus, .grd-field-select:focus { border-color: #8B5CF6; background: #fff; }
    .grd-form-actions { display: flex; gap: 10px; justify-content: flex-end; }
    .grd-btn-cancel {
      padding: 10px 20px; border: 1.5px solid #EDE9E3; border-radius: 100px;
      background: transparent; color: #6B7280; font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s;
    }
    .grd-btn-cancel:hover { border-color: #D1D5DB; }
    .grd-btn-save {
      padding: 10px 24px; border: none; border-radius: 100px;
      background: linear-gradient(135deg, #8B5CF6, #38BDF8);
      color: #fff; font-family: 'Plus Jakarta Sans', sans-serif;
      font-size: 14px; font-weight: 700; cursor: pointer;
      box-shadow: 0 4px 12px rgba(139,92,246,0.25); transition: opacity 0.2s;
    }
    .grd-btn-save:hover { opacity: 0.88; }
    .grd-btn-save:disabled { opacity: 0.6; cursor: not-allowed; }

    /* COMPLETE STATE */
    .grd-complete {
      background: linear-gradient(135deg, rgba(34,197,94,0.06), rgba(34,197,94,0.02));
      border: 1.5px solid rgba(34,197,94,0.2); border-radius: 16px;
      padding: 20px 24px; margin-bottom: 24px;
      display: flex; align-items: center; gap: 16px;
    }
    .grd-complete-icon { font-size: 32px; }
    .grd-complete-title { font-size: 15px; font-weight: 700; color: #15803D; }
    .grd-complete-sub { font-size: 13px; color: #16A34A; margin-top: 2px; }

    /* ALERTS */
    .grd-error {
      background: rgba(239,68,68,0.06); border: 1px solid rgba(239,68,68,0.2);
      border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #DC2626;
      margin-bottom: 16px;
    }
    .grd-success {
      background: rgba(34,197,94,0.06); border: 1px solid rgba(34,197,94,0.2);
      border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #16A34A;
      margin-bottom: 16px;
    }

    /* EMAIL PREVIEW */
    .grd-email-preview {
      background: #F8F7FB; border: 1px solid #EAE5F5; border-radius: 16px;
      padding: 24px; margin-top: 28px;
    }
    .grd-email-preview-label {
      font-size: 11px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;
      color: #9CA3AF; margin-bottom: 16px; display: flex; align-items: center; gap: 8px;
    }
    .grd-email-preview-label::before { content: '📧'; }
    .grd-email-card {
      background: #fff; border: 1px solid #EDE9E3; border-radius: 12px;
      padding: 24px; font-size: 14px; color: #374151; line-height: 1.7;
    }
    .grd-email-subject { font-weight: 700; color: #1E1040; font-size: 15px; margin-bottom: 16px; padding-bottom: 12px; border-bottom: 1px solid #F3F4F6; }
    .grd-email-logo { font-family: 'Fraunces', serif; font-weight: 900; font-size: 18px; color: #1E1040; margin-bottom: 20px; display: block; }
    .grd-email-logo span { color: #8B5CF6; font-style: italic; }
    .grd-email-body p { margin-bottom: 12px; }
    .grd-email-body strong { color: #1E1040; }
    .grd-email-cta {
      display: inline-block; margin: 16px 0;
      background: linear-gradient(135deg, #8B5CF6, #38BDF8);
      color: #fff; padding: 12px 28px; border-radius: 100px;
      font-weight: 700; font-size: 14px; text-decoration: none;
    }
    .grd-email-footer { margin-top: 20px; padding-top: 16px; border-top: 1px solid #F3F4F6; font-size: 12px; color: #9CA3AF; }
  `;

  if (loading) return (
    <div style={{padding: 40, color: '#6B7280', fontSize: 14}}>Loading...</div>
  );

  const slotsToShow = guardianCount - guardians.length;
  const ownerName = session.user.user_metadata?.full_name || session.user.email.split('@')[0];

  return (
    <>
      <style>{style}</style>
      <div className="grd">

        <div className="grd-header">
          <div className="grd-eyebrow">Security</div>
          <h1 className="grd-title">Vault <em>Guardians</em></h1>
          <p className="grd-sub">
            Vault Guardians are the trusted people who can approve access to your vault when it's needed. No one can open your vault alone — at least 3 must agree.
          </p>
        </div>

        {/* HOW IT WORKS */}
        <div className="grd-explainer">
          <div className="grd-explainer-title">How the Guardian System Works</div>
          <div className="grd-explainer-steps">
            {[
              { n: '1', text: <><strong>You designate 3 or 4 trusted people</strong> — a spouse, attorney, adult child, or close friend. They'll receive an email explaining their role.</> },
              { n: '2', text: <><strong>They create a free Merq Legacy account</strong> and accept their role. They have no access to your vault until a request is made.</> },
              { n: '3', text: <><strong>When access is needed,</strong> a Guardian submits a request. The other Guardians are notified and must approve.</> },
              { n: '4', text: <><strong>Once {thresholdText} Guardians approve,</strong> the vault opens. No lawyers, no paperwork, no delays — just the people you trust.</> },
            ].map((s, i) => (
              <div key={i} className="grd-step">
                <div className="grd-step-num">{s.n}</div>
                <div className="grd-step-text">{s.text}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RULE SELECTOR */}
        <div className="grd-rule-wrap">
          <div className="grd-rule-label">How many Vault Guardians do you want?</div>
          <div className="grd-rule-options">
            {[
              { count: 3, title: '3 Guardians', sub: '3 of 3 must approve. Maximum security — all must agree.' },
              { count: 4, title: '4 Guardians', sub: '3 of 4 must approve. One Guardian can be unavailable.' },
            ].map(opt => (
              <div
                key={opt.count}
                className={`grd-rule-option${guardianCount === opt.count ? ' selected' : ''}`}
                onClick={() => handleUpdateCount(opt.count)}
              >
                <div className="grd-rule-option-title">{opt.title}</div>
                <div className="grd-rule-option-sub">{opt.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ALERTS */}
        {error && <div className="grd-error">{error}</div>}
        {success && <div className="grd-success">✓ {success}</div>}

        {/* COMPLETE BANNER */}
        {isSetupComplete && (
          <div className="grd-complete">
            <div className="grd-complete-icon">🛡️</div>
            <div>
              <div className="grd-complete-title">Vault Guardian System Active</div>
              <div className="grd-complete-sub">{thresholdText} Guardians must approve before anyone can access your vault.</div>
            </div>
          </div>
        )}

        {/* GUARDIAN LIST */}
        <div className="grd-list-header">
          <div className="grd-list-title">Your Vault Guardians</div>
          <div className={`grd-list-count${isSetupComplete ? ' ready' : ''}`}>
            {guardians.length} of {guardianCount} added{isSetupComplete ? ' ✓' : ''}
          </div>
        </div>

        <div className="grd-list">
          {/* Existing guardians */}
          {guardians.map((g) => (
            <div key={g.id} className={`grd-card${g.status === 'accepted' ? ' accepted' : ''}`}>
              <div className="grd-card-avatar">
                {g.guardian_name?.split(' ').map(n => n[0]?.toUpperCase()).filter(Boolean).slice(0,2).join('') || '?'}
              </div>
              <div className="grd-card-info">
                <div className="grd-card-name">{g.guardian_name}</div>
                <div className="grd-card-meta">{g.guardian_relationship} · {g.guardian_email}</div>
              </div>
              <div className="grd-card-right">
                <span className={`grd-badge ${g.status}`}>
                  {g.status === 'invited' ? 'Invite Sent' : g.status === 'accepted' ? 'Accepted ✓' : 'Declined'}
                </span>
                {g.status === 'invited' && (
                  <button
                    className="grd-invite-btn"
                    disabled={sendingInvite === g.id}
                    onClick={() => handleSendInvite(g)}
                  >
                    {sendingInvite === g.id ? 'Sending...' : 'Resend'}
                  </button>
                )}
                <button className="grd-remove-btn" onClick={() => handleRemove(g.id, g.guardian_name)}>×</button>
              </div>
            </div>
          ))}

          {/* Empty slots */}
          {guardians.length < guardianCount && !showForm && (
            [...Array(slotsToShow)].map((_, i) => (
              <div key={i} className="grd-empty-slot" onClick={() => setShowForm(true)}>
                <div className="grd-empty-slot-icon">+</div>
                <div>
                  <div className="grd-empty-slot-text">Add a Vault Guardian</div>
                  <div className="grd-empty-slot-sub">Click to add a trusted person</div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* ADD FORM */}
        {showForm && guardians.length < guardianCount && (
          <form className="grd-form" onSubmit={handleAddGuardian}>
            <div className="grd-form-title">Add a Vault Guardian</div>
            <div className="grd-form-grid">
              <div className="grd-field">
                <label className="grd-field-label">Full Name</label>
                <input className="grd-field-input" type="text" placeholder="Jane Smith"
                  value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
              </div>
              <div className="grd-field">
                <label className="grd-field-label">Email Address</label>
                <input className="grd-field-input" type="email" placeholder="jane@example.com"
                  value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
              </div>
              <div className="grd-field">
                <label className="grd-field-label">Relationship</label>
                <select className="grd-field-select" value={form.relationship}
                  onChange={e => setForm({...form, relationship: e.target.value})}>
                  {RELATIONSHIPS.map(r => <option key={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div className="grd-form-actions">
              <button type="button" className="grd-btn-cancel" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="grd-btn-save" disabled={saving}>
                {saving ? 'Adding...' : 'Add Guardian & Send Invite'}
              </button>
            </div>
          </form>
        )}

        {/* EMAIL PREVIEW */}
        <div className="grd-email-preview">
          <div className="grd-email-preview-label">Invite Email Preview — What your Guardians will receive</div>
          <div className="grd-email-card">
            <div className="grd-email-subject">
              You've been named a Vault Guardian by {ownerName}
            </div>
            <span className="grd-email-logo">Merq<span>Legacy</span></span>
            <div className="grd-email-body">
              <p>Hello,</p>
              <p>
                <strong>{ownerName}</strong> has designated you as a <strong>Vault Guardian</strong> for their Merq Legacy vault — a secure digital place where families store everything that matters most, so their loved ones are never left scrambling.
              </p>
              <p>
                <strong>What does this mean?</strong> As a Vault Guardian, you are one of a small group of trusted people who can approve access to their vault when it's truly needed — in the event of their passing or incapacitation.
              </p>
              <p>
                <strong>You don't have access to the vault right now.</strong> No one does, except {ownerName} themselves. The vault only opens when at least 3 Guardians approve an access request together. No single person can open it alone — that's by design.
              </p>
              <p>
                <strong>What we ask of you:</strong> Simply accept this role, create your free Merq Legacy account, and keep your contact information current. When the time comes — if it ever does — you'll receive a notification to approve or deny an access request.
              </p>
              <p>This is an act of trust. Thank you for being someone {ownerName} trusts with something this important.</p>
              <a className="grd-email-cta" href="https://merqlegacy.com">Accept Your Role →</a>
              <div className="grd-email-footer">
                Merq Legacy · Privacy-first estate organization for families<br />
                hello@getmerq.com · merqlegacy.com<br /><br />
                This email was sent because {ownerName} designated you as a Vault Guardian. If you have questions, reply to this email.
              </div>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
